import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { lootboxAPI } from '../../../services/api';
import { fetchNotifications } from '../../../store/slices/countersSlice';
import i18n from '../../../i18n';

export const fetchLootboxChestTypes = createAsyncThunk(
  'lootbox/fetchChestTypes',
  async (_, { rejectWithValue }) => {
    try {
      return await lootboxAPI.getChestTypes();
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message);
    }
  }
);

export const fetchLootboxInventory = createAsyncThunk(
  'lootbox/fetchInventory',
  async (_, { rejectWithValue }) => {
    try {
      return await lootboxAPI.getInventory();
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message);
    }
  }
);

export const fetchLootboxKeyTypes = createAsyncThunk(
  'lootbox/fetchKeyTypes',
  async (_, { rejectWithValue }) => {
    try {
      return await lootboxAPI.getKeyTypes();
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message);
    }
  }
);

// Drop tables of all chests in one request (shown in the chest popup and on the wheel)
export const fetchLootboxContents = createAsyncThunk(
  'lootbox/fetchContents',
  async (_, { rejectWithValue }) => {
    try {
      return await lootboxAPI.getAllChestContents();
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message);
    }
  }
);

export const openChest = createAsyncThunk(
  'lootbox/openChest',
  async (chestTypeId, { rejectWithValue, dispatch }) => {
    try {
      const idempotencyKey = `lb-${chestTypeId}-${Date.now()}`;
      const result = await lootboxAPI.openChest(chestTypeId, idempotencyKey);
      dispatch(fetchLootboxInventory());
      // Inventory badge = openable chests; it changes after every open
      dispatch(fetchNotifications());
      return result;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message);
    }
  }
);

const initialState = {
  chestTypes: [],
  keyTypes: [],
  // { [chestTypeId]: ChestContentInfo[] }
  contentsByChestType: {},
  contentsLoaded: false,
  // Каталог сундуков получен хотя бы раз (для прелоадера на вкладке Cases)
  typesLoaded: false,
  chests: [],
  keys: [],
  lastDrop: null,
  isLoading: false,
  openingChestId: null,
  error: null,
};

const lootboxSlice = createSlice({
  name: 'lootbox',
  initialState,
  reducers: {
    clearLastDrop: (state) => { state.lastDrop = null; },
    clearError: (state) => { state.error = null; },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchLootboxChestTypes.fulfilled, (state, action) => {
        state.chestTypes = action.payload || [];
        state.typesLoaded = true;
      })
      .addCase(fetchLootboxChestTypes.rejected, (state) => {
        state.typesLoaded = true;
      })
      .addCase(fetchLootboxKeyTypes.fulfilled, (state, action) => {
        state.keyTypes = action.payload || [];
      })
      .addCase(fetchLootboxContents.fulfilled, (state, action) => {
        const map = {};
        for (const row of action.payload || []) map[row.chestTypeId] = row.contents || [];
        state.contentsByChestType = map;
        state.contentsLoaded = true;
      })
      .addCase(fetchLootboxContents.rejected, (state) => {
        state.contentsLoaded = true;
      })
      .addCase(fetchLootboxInventory.fulfilled, (state, action) => {
        state.chests = action.payload?.chests || [];
        state.keys = action.payload?.keys || [];
      })
      .addCase(openChest.pending, (state, action) => {
        state.openingChestId = action.meta.arg;
        state.error = null;
      })
      .addCase(openChest.fulfilled, (state, action) => {
        state.openingChestId = null;
        state.lastDrop = action.payload;
      })
      .addCase(openChest.rejected, (state, action) => {
        state.openingChestId = null;
        state.error = action.payload || i18n.t('lootbox.open_failed', 'Failed to open chest');
      });
  },
});

export const { clearLastDrop, clearError } = lootboxSlice.actions;
export default lootboxSlice.reducer;
